import { useCallback } from 'react'
import { matchPath, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'

export type ViewTab = 'ascendency' | 'maps' | 'occupations' | 'notableevents'

const VIEW_TABS = new Set<ViewTab>(['ascendency', 'maps', 'occupations', 'notableevents'])

export function getViewPath(tab: ViewTab, mainId?: string | null, sideItemId?: string | null, treeName?: string | null): string {
  const segments = treeName ? [`/${encodeURIComponent(treeName)}`, 'view'] : ['/view']
  if (mainId) {
    segments.push(encodeURIComponent(mainId))
  }
  segments.push(tab)
  if (mainId && sideItemId) {
    segments.push(encodeURIComponent(sideItemId))
  }
  return segments.join('/')
}

/** A person's page: their drawer, before any main person is chosen. Kept in
 * step with the `person/:sideItem` route in App.tsx. */
const PERSON_ROUTE = '/:treeName/person/:sideItem'

export function getPersonPath(personId: string, treeName?: string | null): string {
  const prefix = treeName ? `/${encodeURIComponent(treeName)}` : ''
  return `${prefix}/person/${encodeURIComponent(personId)}`
}

/** A person's drawer over a story, so its reader stays in the story. Kept in
 * step with the `recits/:storyId/person/:sideItem` route in App.tsx. */
const STORY_PERSON_ROUTE = '/:treeName/recits/:storyId/person/:sideItem'

export function getStoryPath(storyId: string, treeName: string): string {
  return `/${encodeURIComponent(treeName)}/recits/${encodeURIComponent(storyId)}`
}

export function getStoryPersonPath(storyId: string, personId: string, treeName: string): string {
  return `${getStoryPath(storyId, treeName)}/person/${encodeURIComponent(personId)}`
}

/** The person a path inside the application shows on their own page, if any. */
export function matchPersonPath(pathname: string): { treeName: string; personId: string } | null {
  const person = matchPath(PERSON_ROUTE, pathname)
  if (!person?.params.treeName || !person.params.sideItem) return null
  return { treeName: person.params.treeName, personId: person.params.sideItem }
}

export function getViewRouteFromPathname(pathname: string): {
  tab: ViewTab | null
  mainId: string | null
  sideItemId: string | null
  treeName: string | null
  /** The story a person's drawer is open over. */
  storyId?: string | null
} {
  const person = matchPath(PERSON_ROUTE, pathname)
  if (person) {
    return { tab: null, mainId: null, sideItemId: person.params.sideItem ?? null, treeName: person.params.treeName ?? null }
  }
  const storyPerson = matchPath(STORY_PERSON_ROUTE, pathname)
  if (storyPerson) {
    return {
      tab: null,
      mainId: null,
      sideItemId: storyPerson.params.sideItem ?? null,
      treeName: storyPerson.params.treeName ?? null,
      storyId: storyPerson.params.storyId ?? null,
    }
  }
  const segments = pathname.split('/').filter(Boolean).map(decodeURIComponent)
  const viewIndex = segments.indexOf('view')
  if (viewIndex === -1) {
    return { tab: null, mainId: null, sideItemId: null, treeName: null }
  }

  const treeName = viewIndex > 0 ? segments[viewIndex - 1] ?? null : null
  const routeSegments = segments.slice(viewIndex + 1)

  const tabIndex = routeSegments.findIndex((segment) => VIEW_TABS.has(segment as ViewTab))
  if (tabIndex === -1) {
    return { tab: null, mainId: null, sideItemId: null, treeName }
  }

  const tab = routeSegments[tabIndex] as ViewTab
  const mainId = tabIndex > 0 ? routeSegments[tabIndex - 1] ?? null : null
  const explicitSideItemId = routeSegments[tabIndex + 1] ?? null
  const sideItemId = explicitSideItemId ?? mainId

  return {
    tab,
    mainId,
    sideItemId,
    treeName,
  }
}

function withoutLegacyRouteParams(searchParams: URLSearchParams): URLSearchParams {
  const nextSearchParams = new URLSearchParams(searchParams)
  nextSearchParams.delete('main')
  nextSearchParams.delete('sideItem')
  return nextSearchParams
}

export function useViewRoute() {
  const { main, sideItem, treeName } = useParams<{ main?: string; sideItem?: string; treeName?: string }>()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const route = getViewRouteFromPathname(location.pathname)
  const activeTreeName = treeName ?? route.treeName ?? null
  const mainId = route.tab ? route.mainId : main ?? searchParams.get('main')
  const sideItemId = route.tab
    ? route.sideItemId ?? searchParams.get('sideItem') ?? mainId
    : sideItem ?? route.sideItemId ?? mainId ?? searchParams.get('sideItem')

  const navigateTo = useCallback((
    nextMainId: string | null,
    nextSideItemId: string | null,
    options: { clearSearch?: boolean; replace?: boolean } = {},
  ) => {
    const nextSearchParams = options.clearSearch
      ? new URLSearchParams()
      : withoutLegacyRouteParams(searchParams)
    const nextSearch = nextSearchParams.toString()
    // A person without a main one has their own page, or their drawer over
    // the story being read; with a main person, the current tab (the fan
    // chart from anywhere else).
    const pathname = !nextMainId && nextSideItemId
      ? route.storyId && activeTreeName
        ? getStoryPersonPath(route.storyId, nextSideItemId, activeTreeName)
        : getPersonPath(nextSideItemId, activeTreeName)
      : getViewPath(route.tab ?? 'ascendency', nextMainId, nextSideItemId, activeTreeName)
    void navigate({
      pathname,
      search: nextSearch ? `?${nextSearch}` : '',
      hash: location.hash,
    }, { replace: options.replace })
  }, [activeTreeName, location.hash, navigate, route.storyId, route.tab, searchParams])

  const setMainId = useCallback((nextMainId: string) => {
    navigateTo(nextMainId, sideItemId)
  }, [navigateTo, sideItemId])

  const setSideItemId = useCallback((nextSideItemId: string) => {
    navigateTo(mainId, nextSideItemId)
  }, [mainId, navigateTo])

  return {
    ...route,
    treeName: activeTreeName,
    mainId,
    sideItemId,
    navigateTo,
    setMainId,
    setSideItemId,
  }
}